import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, MailCheck, ShieldCheck } from 'lucide-react';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { Navigation } from '@/components/Navigation';
import { lovable } from '@/integrations/lovable/index';
import { toast } from 'sonner';
import logo from '@/assets/gaius-logo.webp';

const loginSchema = z.object({
  email: z.string().trim().email("Adresse email invalide"),
  password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

const signupSchema = z.object({
  fullName: z.string().trim().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().trim().email("Adresse email invalide"),
  password: z.string()
    .min(8, "Le mot de passe doit contenir au moins 8 caractères")
    .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
    .regex(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule")
    .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

export const AuthPage = () => {
  const { t, language } = useLanguage();
  const { user, loading, signIn, signUp, pending2FA, verify2FA, cancel2FA } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'signup' ? 'signup' : 'login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  
  // Form states
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      toast.error(language === 'fr' ? 'Erreur de connexion Google' : 'Google sign-in error');
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      loginSchema.parse({ email: loginEmail, password: loginPassword });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
    }
    
    setIsSubmitting(true);
    const { error, needs2FA } = await signIn(loginEmail, loginPassword);
    setIsSubmitting(false);
    
    if (!error && !needs2FA) {
      navigate('/dashboard');
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length < 8) return;
    
    setIsSubmitting(true);
    const { error } = await verify2FA(otpCode);
    setIsSubmitting(false);
    
    if (!error) {
      navigate('/dashboard');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      signupSchema.parse({
        fullName: signupName,
        email: signupEmail,
        password: signupPassword,
        confirmPassword: signupConfirmPassword,
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
    }
    
    setIsSubmitting(true);
    const { error, needsConfirmation } = await signUp(signupEmail, signupPassword, signupName);
    setIsSubmitting(false);
    
    if (!error && needsConfirmation) {
      setConfirmationEmail(signupEmail);
      setEmailConfirmationSent(true);
    } else if (!error) {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (emailConfirmationSent) {
    return (
      <>
        <Navigation />
        <div className="min-h-[calc(100vh-5rem)] bg-secondary/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <SEOHead
            title={language === 'fr' ? "Vérifiez votre email | Gaius" : "Check your email | Gaius"}
            description={language === 'fr' ? "Confirmez votre adresse email pour activer votre compte Gaius" : "Confirm your email to activate your Gaius account"}
          />
          <div className="w-full max-w-md space-y-8">
            <Card className="border-border shadow-lg">
              <CardContent className="pt-8 pb-8 text-center space-y-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <MailCheck className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    {language === 'fr' ? 'Vérifiez votre boîte mail' : 'Check your inbox'}
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr'
                      ? `Un email de confirmation a été envoyé à`
                      : `A confirmation email has been sent to`}
                  </p>
                  <p className="text-foreground font-medium mt-1">{confirmationEmail}</p>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {language === 'fr'
                    ? 'Cliquez sur le lien dans l\'email pour activer votre compte. Si vous ne le voyez pas, vérifiez vos spams.'
                    : 'Click the link in the email to activate your account. If you don\'t see it, check your spam folder.'}
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setEmailConfirmationSent(false);
                    setActiveTab('login');
                  }}
                >
                  {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // 2FA OTP verification screen
  if (pending2FA) {
    return (
      <>
        <Navigation />
        <div className="min-h-[calc(100vh-5rem)] bg-secondary/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <SEOHead
            title={language === 'fr' ? "Vérification | Gaius" : "Verification | Gaius"}
            description={language === 'fr' ? "Entrez le code de vérification" : "Enter your verification code"}
          />
          <div className="w-full max-w-md space-y-8">
            <Card className="border-border shadow-lg">
              <CardContent className="pt-8 pb-8 text-center space-y-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    {language === 'fr' ? 'Vérification en deux étapes' : 'Two-step verification'}
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr'
                      ? `Un code de vérification a été envoyé à`
                      : `A verification code has been sent to`}
                  </p>
                  <p className="text-foreground font-medium mt-1">{pending2FA.email}</p>
                </div>
                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="otp-code">{language === 'fr' ? 'Code de vérification' : 'Verification code'}</Label>
                    <Input
                      id="otp-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      placeholder="00000000"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center text-2xl tracking-widest font-mono"
                      autoFocus
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting || otpCode.length < 8}>
                    {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {language === 'fr' ? 'Vérifier' : 'Verify'}
                  </Button>
                </form>
                <Button
                  variant="ghost"
                  className="w-full text-sm text-muted-foreground"
                  onClick={() => { cancel2FA(); setOtpCode(''); }}
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-[calc(100vh-5rem)] bg-secondary/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <SEOHead
          title={language === 'fr' ? "Connexion | Gaius" : "Login | Gaius"}
          description={language === 'fr' ? "Connectez-vous à votre espace formation Gaius" : "Login to your Gaius training dashboard"}
        />
        
        <div className="w-full max-w-md space-y-8">
          <Card className="border-border shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-foreground">
                {activeTab === 'login' ? t('loginTitle') : t('registerTitle')}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {activeTab === 'login' ? t('loginSubtitle') : t('registerSubtitle')}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login">{t('login')}</TabsTrigger>
                  <TabsTrigger value="signup">{t('register')}</TabsTrigger>
                </TabsList>
                
                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t('emailLabel')}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className={errors.email ? 'border-destructive' : ''}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">{t('passwordLabel')}</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className={errors.password ? 'border-destructive' : ''}
                      />
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                    </div>

                    <div className="text-right">
                      <Link to={language === 'fr' ? '/mot-de-passe-oublie' : '/forgot-password'} className="text-sm text-primary hover:underline">
                        {t('forgotPassword')}
                      </Link>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      {t('login')}
                    </Button>
                  </form>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">{language === 'fr' ? 'ou' : 'or'}</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                  >
                    {googleLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    {language === 'fr' ? 'Continuer avec Google' : 'Continue with Google'}
                  </Button>

                  <div className="mt-4 text-center">
                    <button 
                      type="button"
                      className="text-sm text-primary hover:underline"
                      onClick={() => setActiveTab('signup')}
                    >
                      {t('noAccount')} {t('register')}
                    </button>
                  </div>
                </TabsContent>
                
                {/* Signup Tab */}
                <TabsContent value="signup">
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">{t('contactName')}</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="Jean Dupont"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        className={errors.fullName ? 'border-destructive' : ''}
                      />
                      {errors.fullName && (
                        <p className="text-sm text-destructive">{errors.fullName}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">{t('emailLabel')}</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        className={errors.email ? 'border-destructive' : ''}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">{t('passwordLabel')}</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="••••••••"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        className={errors.password ? 'border-destructive' : ''}
                      />
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                      <PasswordStrengthIndicator password={signupPassword} />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm">{t('confirmPasswordLabel')}</Label>
                      <Input
                        id="signup-confirm"
                        type="password"
                        placeholder="••••••••"
                        value={signupConfirmPassword}
                        onChange={(e) => setSignupConfirmPassword(e.target.value)}
                        className={errors.confirmPassword ? 'border-destructive' : ''}
                      />
                      {errors.confirmPassword && (
                        <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                      )}
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {t('register')}
                    </Button>
                  </form>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">{language === 'fr' ? 'ou' : 'or'}</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                  >
                    {googleLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    {language === 'fr' ? 'Continuer avec Google' : 'Continue with Google'}
                  </Button>

                  <div className="mt-4 text-center">
                    <button 
                      type="button"
                      className="text-sm text-primary hover:underline"
                      onClick={() => setActiveTab('login')}
                    >
                      {t('haveAccount')} {t('login')}
                    </button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
